<template>
  <component :class="classes" :to="to" :href="href" :is="is"
             :disabled="disabled && !to">
    <slot></slot>
  </component>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { RouteLocation } from 'vue-router'

const colors = ['primary', 'secondary', 'danger', 'success', 'warning', 'info', 'light', 'dark']

export default defineComponent({
  name: 'Btn',
  props: {
    block: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      validate: (value: string) => colors.includes(value),
    },
    to: {
      type: Object as PropType<RouteLocation>,
    },
    href: {
      type: String,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  setup(props) {
    const classes = computed(() => {
      const c = ['ui-btn']
      if (props.block) {
        c.push('ui-btn--block')
      }
      if (props.color && colors.includes(props.color)) {
        c.push(`ui-btn--${props.color}`)
      }
      if (props.disabled) {
        c.push('ui-btn--disabled')
      }
      return c
    })
    const is = computed(() => {
      if (props.to) {
        return 'router-link'
      }
      if (props.href) {
        return 'a'
      }
      return 'button'
    })
    return {
      classes,
      is,
    }
  },
})
</script>

<style lang="stylus">
.ui-btn
  font-size .9rem
  font-weight 500
  line-height 1.5
  display inline-flex
  padding .375rem .75rem
  user-select none
  transition \ color .15s ease-in-out, \
  background-color .15s ease-in-out, \
  border-color .15s ease-in-out, \
  box-shadow .15s ease-in-out
  text-align center
  vertical-align middle
  align-items center
  justify-content center
  color var(--dark-font-color)
  border 1px solid transparent
  border-radius .25rem
  background-color transparent
  box-shadow 0 2px 8px 0 rgba(0, 0, 0, .1)
  cursor pointer
  text-decoration none
  margin 0 .5rem .5rem
  outline none

  &--block
    display flex
    width 100%
    margin 0 0 .5rem

    &:last-child
      margin-bottom 0

  &--primary
    color #fff
    border-color var(--primary-color)
    background-color var(--primary-color)

    &:hover
      border-color var(--primary-dark-color)
      background-color var(--primary-dark-color)
      color #fff

  &--secondary
    color #fff
    border-color var(--secondary-color)
    background-color var(--secondary-color)

    &:hover
      border-color var(--secondary-dark-color)
      background-color var(--secondary-dark-color)
      color #fff

  &--success
    color #fff
    border-color var(--success-color)
    background-color var(--success-color)

    &:hover
      border-color var(--success-dark-color)
      background-color var(--success-dark-color)
      color #fff

  &--danger
    color #fff
    border-color var(--danger-color)
    background-color var(--danger-color)

    &:hover
      border-color var(--danger-dark-color)
      background-color var(--danger-dark-color)
      color #fff

  &--warning
    color #fff
    border-color var(--warning-color)
    background-color var(--warning-color)

    &:hover
      border-color var(--warning-dark-color)
      background-color var(--warning-dark-color)
      color #fff

  &--info
    color #fff
    border-color var(--info-color)
    background-color var(--info-color)

    &:hover
      border-color var(--info-dark-color)
      background-color var(--info-dark-color)
      color #fff

  &--dark
    color #fff
    border-color var(--dark-color)
    background-color var(--dark-color)

    &:hover
      border-color var(--info-darker-color)
      background-color var(--info-darker-color)
      color #fff

  &--light
    border-color var(--light-color)
    background-color var(--light-color)

    &:hover
      border-color var(--light-dark-color)
      background-color var(--light-dark-color)
      color #fff

  &--disabled
    opacity .65
</style>
